import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Truck, Edit, Trash2, History, Search, AlertTriangle } from 'lucide-react';
import { fleetAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const EMPTY = { vehicle_number: '', vehicle_type: '', capacity: '', owner_name: '', owner_mobile: '',
  registration_number: '', insurance_number: '', insurance_expiry: '', fitness_expiry: '',
  permit_expiry: '', pollution_expiry: '', gps_enabled: false, status: 'active', notes: '' };

const EXPIRY_KEYS = [['insurance_expiry', 'Insurance'], ['fitness_expiry', 'Fitness'], ['permit_expiry', 'Permit'], ['pollution_expiry', 'Pollution']];

export function expiryBadges(v) {
  const today = new Date().toISOString().split('T')[0];
  const soon = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
  const out = [];
  EXPIRY_KEYS.forEach(([k, label]) => {
    const d = v[k];
    if (!d) return;
    if (d < today) out.push({ label: `${label} expired`, cls: 'bg-rose-50 text-rose-600' });
    else if (d <= soon) out.push({ label: `${label} expiring`, cls: 'bg-amber-50 text-amber-600' });
  });
  return out;
}

export default function VehiclesPage() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const isAdmin = ['admin', 'owner', 'super_admin'].includes(user?.role);
  const canManage = isAdmin || (user?.permissions || []).includes('vehicle:create');

  const load = useCallback(() => {
    setLoading(true);
    fleetAPI.vehicles.list({ search: search || undefined })
      .then(r => setVehicles(r.data?.vehicles || []))
      .catch(() => toast.error('Failed to load vehicles'))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const u = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.vehicle_number.trim()) { toast.error('Vehicle number is required'); return; }
    setSaving(true);
    try {
      if (editing) { await fleetAPI.vehicles.update(editing, form); toast.success('Vehicle updated'); }
      else { await fleetAPI.vehicles.create(form); toast.success('Vehicle added'); }
      setShowForm(false); setEditing(null); setForm({ ...EMPTY }); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const openEdit = (v) => {
    const f = { ...EMPTY };
    Object.keys(EMPTY).forEach(k => { f[k] = v[k] ?? EMPTY[k]; });
    setForm(f); setEditing(v.vehicle_id); setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this vehicle? Historical LRs will keep their vehicle details.')) return;
    try { await fleetAPI.vehicles.remove(id); toast.success('Vehicle deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="vehicles-page" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2"><Truck className="w-6 h-6 text-purple-600" /> Vehicle Master</h1>
          <p className="text-sm text-slate-500 mt-1">Centralized fleet records used across LRs and reports.</p>
        </div>
        {canManage && <Button onClick={() => { setForm({ ...EMPTY }); setEditing(null); setShowForm(true); }} data-testid="add-vehicle-btn"><Plus className="w-4 h-4 mr-1" /> Add Vehicle</Button>}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input className="form-input pl-9" placeholder="Search by number, owner, type..." value={search} onChange={e => setSearch(e.target.value)} data-testid="vehicle-search" />
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md overflow-x-auto">
        <table className="w-full data-table">
          <thead><tr><th>Vehicle No.</th><th>Type</th><th>Capacity</th><th>Owner</th><th>Owner Mobile</th><th>Alerts</th><th>Status</th><th className="text-right">Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="8" className="text-center py-8 text-slate-400">Loading...</td></tr>
            : vehicles.length === 0 ? <tr><td colSpan="8" className="text-center py-8 text-slate-400">No vehicles yet. Add your first vehicle.</td></tr>
            : vehicles.map(v => {
              const badges = expiryBadges(v);
              return (
                <tr key={v.vehicle_id} data-testid={`vehicle-row-${v.vehicle_id}`}>
                  <td className="font-mono font-medium">{v.vehicle_number}</td>
                  <td>{v.vehicle_type || '—'}</td>
                  <td>{v.capacity || '—'}</td>
                  <td>{v.owner_name || '—'}</td>
                  <td className="font-mono text-xs">{v.owner_mobile || '—'}</td>
                  <td>
                    {badges.length === 0 ? <span className="text-slate-300 text-xs">—</span> :
                      <div className="flex flex-wrap gap-1">{badges.map((b, i) => <span key={i} className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${b.cls}`}><AlertTriangle className="w-2.5 h-2.5" />{b.label}</span>)}</div>}
                  </td>
                  <td><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase ${v.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>{v.status}</span></td>
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/vehicles/${v.vehicle_id}`} className="p-1.5 hover:bg-purple-50 rounded" title="Trip History" data-testid={`vehicle-history-${v.vehicle_id}`}><History className="w-4 h-4 text-purple-600" /></Link>
                      {canManage && <button onClick={() => openEdit(v)} className="p-1.5 hover:bg-slate-100 rounded" data-testid={`vehicle-edit-${v.vehicle_id}`}><Edit className="w-4 h-4 text-slate-500" /></button>}
                      {isAdmin && <button onClick={() => remove(v.vehicle_id)} className="p-1.5 hover:bg-rose-50 rounded" data-testid={`vehicle-delete-${v.vehicle_id}`}><Trash2 className="w-4 h-4 text-rose-500" /></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={(o) => !o && setShowForm(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vehicle-form-modal">
          <DialogHeader><DialogTitle>{editing ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="form-label">Vehicle Number *</label><input className="form-input" value={form.vehicle_number} onChange={e => u('vehicle_number', e.target.value.toUpperCase())} placeholder="DL01AB1234" data-testid="vehicle-number-input" /></div>
            <div><label className="form-label">Vehicle Type</label><input className="form-input" value={form.vehicle_type} onChange={e => u('vehicle_type', e.target.value)} placeholder="20FT Truck / Tempo" data-testid="vehicle-type-input" /></div>
            <div><label className="form-label">Capacity</label><input className="form-input" value={form.capacity} onChange={e => u('capacity', e.target.value)} placeholder="9 Ton" /></div>
            <div><label className="form-label">Registration Number</label><input className="form-input" value={form.registration_number} onChange={e => u('registration_number', e.target.value)} /></div>
            <div><label className="form-label">Owner Name</label><input className="form-input" value={form.owner_name} onChange={e => u('owner_name', e.target.value)} data-testid="vehicle-owner-input" /></div>
            <div><label className="form-label">Owner Mobile</label><input className="form-input" value={form.owner_mobile} onChange={e => u('owner_mobile', e.target.value)} /></div>
            <div><label className="form-label">Insurance Number</label><input className="form-input" value={form.insurance_number} onChange={e => u('insurance_number', e.target.value)} /></div>
            <div><label className="form-label">Insurance Expiry</label><input type="date" className="form-input" value={form.insurance_expiry} onChange={e => u('insurance_expiry', e.target.value)} /></div>
            <div><label className="form-label">Fitness Expiry</label><input type="date" className="form-input" value={form.fitness_expiry} onChange={e => u('fitness_expiry', e.target.value)} /></div>
            <div><label className="form-label">Permit Expiry</label><input type="date" className="form-input" value={form.permit_expiry} onChange={e => u('permit_expiry', e.target.value)} /></div>
            <div><label className="form-label">Pollution Expiry</label><input type="date" className="form-input" value={form.pollution_expiry} onChange={e => u('pollution_expiry', e.target.value)} /></div>
            <div><label className="form-label">Status</label><select className="form-input" value={form.status} onChange={e => u('status', e.target.value)} data-testid="vehicle-status-select"><option value="active">Active</option><option value="inactive">Inactive</option></select></div>
            <div className="flex items-center gap-2 pt-5"><input type="checkbox" id="gps" checked={form.gps_enabled} onChange={e => u('gps_enabled', e.target.checked)} className="w-4 h-4" /><label htmlFor="gps" className="text-sm text-slate-600">GPS Enabled</label></div>
            <div className="col-span-2"><label className="form-label">Notes</label><textarea className="form-input min-h-[50px]" value={form.notes} onChange={e => u('notes', e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-vehicle-btn">{saving ? 'Saving...' : editing ? 'Update Vehicle' : 'Add Vehicle'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
